import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import AdminConciergeMagicLoginActions from "./AdminConciergeMagicLoginActions"
import CustomerAccessFields, {
  INITIAL_CUSTOMER_ACCESS_FORM,
  formatCustomerAccessError,
} from "./CustomerAccessFields"

export default function AdminCustomerAccessCreateDialog({
  open,
  onClose,
  onCreate,
  saving,
  campaigns = [],
}) {
  const [form, setForm] = useState(INITIAL_CUSTOMER_ACCESS_FORM)
  const [error, setError] = useState(null)
  const [createdUser, setCreatedUser] = useState(null)

  useEffect(() => {
    if (!open) return
    setForm(INITIAL_CUSTOMER_ACCESS_FORM)
    setError(null)
    setCreatedUser(null)
  }, [open])

  function handleSubmit(event) {
    event.preventDefault()
    setError(null)
    onCreate(form)
      .then((user) => setCreatedUser(user))
      .catch((err) => {
        setError(
          formatCustomerAccessError(err, "Could not create customer access.")
        )
      })
  }

  if (createdUser) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Customer access created</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            <Alert severity="success">
              {createdUser.email} can now sign in with this magic link.
            </Alert>
            {createdUser.customer_login_link ? (
              <>
                <TextField
                  label="Magic login link"
                  value={createdUser.customer_login_link}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  onFocus={(e) => e.target.select()}
                />
                <AdminConciergeMagicLoginActions
                  loginLink={createdUser.customer_login_link}
                  openLabel="Open as customer"
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No login link: the account is inactive. Activate it to get a
                magic link from the Users list.
              </Typography>
            )}
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Create customer access</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Creates the customer account and its magic login link. Replaces
              the Airtable access creation form.
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <CustomerAccessFields
              form={form}
              setForm={setForm}
              campaigns={campaigns}
            />
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            Create access
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AdminCustomerAccessCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  campaigns: PropTypes.array,
}
